import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram, Mail, MapPin, Smartphone } from 'lucide-react';
import TrabdjuLogo from './TrabdjuLogo';
import { TRABADJU_INFO as T } from '@/utils/businessInfo';

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="bg-navy text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <TrabdjuLogo size="lg" variant="light" asLink={false} />
            </Link>
            <p className="text-gray-400 mb-4 max-w-md">{t('footer.tagline')}</p>
            <div className="flex items-center space-x-4 mb-6">
              <a
                href="https://www.instagram.com/trabadju.cv?igsh=d2ZmNGFlZDE4c2xi&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-pink-500 to-purple-600 p-3 rounded-full hover:from-pink-600 hover:to-purple-700 transition-all transform hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5 text-white" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.platform')}</h3>
            <ul className="space-y-2">
              <li><Link to="/jobs/search" className="text-gray-400 hover:text-white transition-colors">{t('nav.searchJobs')}</Link></li>
              <li><Link to="/candidate/register" className="text-gray-400 hover:text-white transition-colors">{t('footer.createCandidate')}</Link></li>
              <li><Link to="/register" className="text-gray-400 hover:text-white transition-colors">{t('footer.postJob')}</Link></li>
              <li><Link to="/sobre" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-sobre">{t('footer.aboutUs')}</Link></li>
              <li><Link to="/contacto" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-contacto">{t('footer.contact')}</Link></li>
              <li>
                <Link to="/instalar" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="footer-link-instalar">
                  <Smartphone className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />
                  {t('footer.installApp')}
                  <span className="text-[10px] font-semibold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-full leading-none">App</span>
                </Link>
              </li>
              <li><Link to="/termos" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-termos">{t('footer.terms')}</Link></li>
              <li><Link to="/privacidade" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-privacidade">{t('footer.privacy')}</Link></li>
              <li><Link to="/politica-devolucoes" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-devolucoes">{t('footer.refundPolicy')}</Link></li>
              <li><Link to="/cookies" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-cookies">{t('footer.cookies')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.contact')}</h3>
            <ul className="space-y-3 text-gray-400 text-sm">
              <li className="flex items-start space-x-2">
                <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <a href={`mailto:${T.email_public}`} className="hover:text-white transition-colors break-all">{T.email_public}</a>
              </li>
              <li className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{T.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-10 pt-6 flex flex-col gap-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{t('footer.acceptedCards')}</span>
              <img src="/payment-logos/vinti4.png" alt="vinti4" className="h-6 w-auto opacity-90" data-testid="footer-logo-vinti4" />
              <img src="/payment-logos/visa.png" alt="Visa Secure" className="h-5 w-auto opacity-90" data-testid="footer-logo-visa" />
              <img src="/payment-logos/mastercard.png" alt="Mastercard ID Check" className="h-5 w-auto opacity-90" data-testid="footer-logo-mastercard" />
              <img src="/payment-logos/amex.png" alt="American Express SafeKey" className="h-5 w-auto opacity-90" data-testid="footer-logo-amex" />
            </div>
            <p className="text-gray-500 text-xs">{t('footer.secureConnection')}</p>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <div className="text-gray-400 text-xs">
              <p>© {new Date().getFullYear()} {T.legal_name} — NIF {T.nif}.</p>
              <p className="mt-1">{t('footer.rightsReserved')}</p>
            </div>
            <p className="text-gray-400 text-xs">{t('footer.madeIn')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
